(ns manage.adset-integration-meta-settings.db
  (:require ["react-hook-form" :as rhf]
            [cljs-bean.core :refer [->clj ->js]]
            [clojure.spec.alpha :as spec]
            [clojure.string :as str]
            [common.form :as form]
            [common.queries :refer [invalidate-queries use-mutation use-query]]
            [common.resource.query.social-settings :as social-settings]
            [common.util :refer [spread-obj]]
            [manage.adset.hooks :refer [use-channel]]
            [mate.core :as mc]
            [re-frame.core :as rf]
            [uix.core :as uix :refer [defhook]]
            [uix.re-frame :refer [use-subscribe]]))

(def entity-name "Adset Meta Settings")

(def channel->initial-value
  {:meta {:facebook-ad-account-id ""
          :facebook-page-id ""
          :facebook-campaign-id ""
          :auto-enable true}
   :tiktok {:tiktok-account-id ""
            :tiktok-identity-id ""
            :tiktok-campaign-id ""
            :tiktok-identity-type ""
            :auto-enable true}})

(spec/def ::non-blank-string (spec/and string? (complement str/blank?)))
(spec/def ::facebook-ad-account-id ::non-blank-string)
(spec/def ::tiktok-account-id ::non-blank-string)
(spec/def ::tiktok-identity-id ::non-blank-string)
(spec/def ::tiktok-identity-type ::non-blank-string)
(spec/def ::tiktok-campaign-id (spec/nilable ::non-blank-string))
(spec/def ::facebook-page-id ::non-blank-string)

(spec/def ::facebook-campaign-id (spec/nilable ::non-blank-string))

(spec/def ::auto-enable boolean?)

(spec/def ::value
  (spec/keys :req-un [::facebook-ad-account-id
                      ::facebook-page-id
                      ::auto-enable]))

(defmulti value-spec :channel)

(defmethod value-spec :meta [_]
  (spec/keys :req-un [::facebook-ad-account-id
                      ::facebook-page-id
                      ::auto-enable]
             :opt-un [::facebook-campaign-id]))

(defmethod value-spec :tiktok [_]
  (spec/keys :req-un [::tiktok-account-id
                      ::tiktok-identity-id
                      ::tiktok-identity-type
                      ::auto-enable]
             :opt-un [::tiktok-campaign-id]))

(spec/def ::value (spec/multi-spec value-spec :channel))

(defn get-spec-problems [integration-settings channel]
  (::spec/problems (spec/explain-data ::value (merge {:channel channel}
                                                     (channel->initial-value channel)
                                                     integration-settings))))

(def ui-messages
  {:success "Successfully saved platform settings"
   :failure "Failed to save platform settings"})

(defn use-integration-settings []
  (let [channel (use-channel)
        url-context (use-subscribe [:url-context])
        integration-settings->initial-value
        (uix/use-callback
         (fn [^js data]
           (let [data (->clj data)]
             (case channel
               "tiktok"
               (-> data
                   (assoc :tiktok-campaign-id (first (:tiktok-campaign-ids data)))
                   (dissoc :tiktok-campaign-ids))
               "meta"
               (-> data
                   (assoc :facebook-campaign-id (first (:facebook-campaign-ids data)))
                   (dissoc :facebook-campaign-ids))
               data)))
         [channel])

        {:keys [data]} (use-query (spread-obj (social-settings/fetch-advertiser-adset-integration-settings
                                               (assoc url-context :channel channel))
                                              #js {:select integration-settings->initial-value}))]
    ;; Merge is needed to have the blank strings for validation
    ;; Clojure spec only specifies the problems of existing keys,
    ;; and one missing key, so not possible to use for showing the
    ;; error messages in the form without adding all keys initially
    (merge (channel->initial-value (keyword channel)) data)))

(defn- tiktok-identity->type [^js data]
  (mc/index-by :id :type (->clj data)))

(defn- ->payload [channel identity-id->type form-data]
  (cond-> form-data
    true (assoc :integration-method "direct")
    (= "tiktok" channel) (assoc :tiktok-identity-type (identity-id->type (:tiktok-identity-id form-data)))
    (= "tiktok" channel) (dissoc :tiktok-campaign-id)

    (and (= "tiktok" channel)
         (seq (:tiktok-campaign-id form-data)))
    (assoc :tiktok-campaign-ids [(:tiktok-campaign-id form-data)])

    (= "meta" channel) (dissoc :facebook-campaign-id)

    (and (= "meta" channel)
         (seq (:facebook-campaign-id form-data)))
    (assoc :facebook-campaign-ids [(:facebook-campaign-id form-data)])))

(defhook use-form-control [{:keys [publish-mode channel]
                            on-submit-success :on-submit}]
  (let [{:keys [advertiser-id adset-id] :as url-context} (use-subscribe [:url-context])
        integration-settings (use-integration-settings)

        {:keys [trigger handleSubmit control] :as form-control}
        (form/use-form #js {:resolver (when publish-mode
                                        (form/spec-resolver
                                         (value-spec {:channel (keyword channel)})))
                            :values (->js integration-settings)
                            :mode "all"})

        tiktok-account-id (rhf/useWatch #js {:name "tiktok-account-id" :control control})

        {identity-id->type :data} (use-query (spread-obj (social-settings/tiktok-identities-query advertiser-id adset-id tiktok-account-id)
                                                         #js {:select tiktok-identity->type
                                                              :enabled (and (boolean (seq tiktok-account-id))
                                                                            (= "tiktok" channel))}))

        {save-integration-settings :mutate} (use-mutation (social-settings/save-integration-settings-mutation
                                                           (assoc url-context :channel channel)))

        on-submit (handleSubmit
                   (fn [form-data]
                     (save-integration-settings
                      (->payload channel identity-id->type (->clj form-data))
                      #js {:onSuccess (fn [_response]
                                        (rf/dispatch [:modal/close])
                                        (rf/dispatch [:ocp/close])
                                        (when on-submit-success (on-submit-success))
                                        (invalidate-queries
                                         {:queryKey ["advertiser-adset-integration-settings"
                                                     advertiser-id adset-id]})
                                        (when (= "tiktok" channel)
                                          (invalidate-queries
                                           {:queryKey ["tiktok-ad-groups" advertiser-id adset-id]}))
                                        (when (= "meta" channel)
                                          (invalidate-queries
                                           {:queryKey ["meta-adsets" advertiser-id adset-id]}))
                                        (rf/dispatch [:response-handler/handle-response
                                                      {:entity-name entity-name
                                                       :type :success
                                                       :message (:success ui-messages)}]))
                           :onError (fn [response]
                                      (rf/dispatch [:response-handler/handle-response
                                                    {:entity-name entity-name
                                                     :type :error
                                                     :message (:failure ui-messages)
                                                     :response response}]))})))]
    ;; show errors initially when publish mode enabled
    (uix/use-effect (fn [] (when publish-mode (trigger)))
                    [publish-mode trigger])
    (assoc form-control :on-submit on-submit)))

(defn get-account-name [account-id accounts]
  (some #(when (= (:id %) account-id) (:description %)) accounts))

(defhook use-account-summary
  [channel {:keys [facebook-ad-account-id tiktok-account-id]}]
  (let [{:keys [advertiser-id]} (use-subscribe [:url-context])
        {accounts :data} (use-query
                          (case channel
                            "meta" (social-settings/social-settings-query advertiser-id)
                            "tiktok" (social-settings/tiktok-accounts-query advertiser-id)))
        account-id (case channel
                     "meta" facebook-ad-account-id
                     "tiktok" tiktok-account-id
                     nil)]
    (str (when-let [account-name (get-account-name account-id accounts)]
           (str account-name " - "))
         account-id)))

(defhook use-settings-details []
  (let [integration-settings (use-integration-settings)
        channel (use-channel)
        summary (use-account-summary channel integration-settings)]
    {:problems (get-spec-problems integration-settings (keyword channel))
     :summary summary}))
